/* eslint-disable react-hooks/exhaustive-deps */
import { Time } from '@eagle/common';
import { Divider, FormControl, InputLabel, MenuItem, Select, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { forwardRef, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTimeRangeChangeHandler } from '../../components/date-time-range';
import { useResponsiveText } from '../../hooks';
import { useSearch } from '../../pages/list/use-search';
import { Undefinable } from '../../types';
import { hourCycle, testid } from '../../util';
import { CustomRange, FixedStartDate, RangeSelectOption, RangeSelectValues } from '../date-time-range-picker';
import { Icon } from '../icons';

interface Props {
  'data-testid'?: string;
  isDateTimePickerOpen: boolean;
  onDateRangeChanged: DateTimeRangeChangeHandler;
  openDateTimePicker: () => void;
  rangeSelection: RangeSelectValues['key'];
  rangeSelectOptions: RangeSelectOption[];
  savedRange: CustomRange;
  setRangeSelection: (value: RangeSelectValues['key']) => void;
}

const StyledRangeSelect = styled(Select)({
  width: 'inherit',
});

export const RangeSelect = forwardRef<HTMLDivElement, Props>(({
  isDateTimePickerOpen,
  onDateRangeChanged,
  openDateTimePicker,
  rangeSelection,
  rangeSelectOptions,
  savedRange,
  setRangeSelection,
  ...props
}, ref): JSX.Element => {
  const hour12 = hourCycle();
  const { t } = useTranslation(['common']);
  const { startDate: savedStartDate, endDate: savedEndDate } = savedRange;
  const { setSavedSelection } = useSearch();
  const isCustomRangeSet = !!(savedStartDate && savedEndDate);
  const [fixedStartDate, setFixedStartDate] = useState<FixedStartDate>();
  const [fixedEndDate, setFixedEndDate] = useState(DateTime.now());

  const subTextNow = t('common:component.range-select.labels.now');
  const textChangeRange = t('common:component.range-select.action.change-custom-range');
  const textLabel = t('common:component.range-select.labels.displaying');
  const textPickRange = t('common:component.range-select.labels.pick-custom-range');

  const renderSubText = (start: Undefinable<DateTime>, end?: DateTime): string => (
    t('common:component.range-select.labels.time-range', {
      start: start?.toLocaleString({ ...DateTime.DATETIME_MED, year: undefined }),
      end: end?.toLocaleString({ ...DateTime.DATETIME_MED, year: undefined }) ?? subTextNow,
    })
  );

  const renderTitle = (option: string): JSX.Element => {
    if (isCustomRangeSet && rangeSelection === RangeSelectValues.PICK_CUSTOM_RANGE.key) return <Box>{isDateTimePickerOpen ? textChangeRange : renderSubText(savedStartDate, savedEndDate)}</Box>;

    const rangeSelectOption = rangeSelectOptions.filter(({ value }) => value.key === option).shift();
    return <Box>{rangeSelectOption?.label ?? textPickRange}</Box>;
  };

  useEffect(() => {
    const updateTime = setInterval(() => {
      setFixedEndDate(DateTime.now());
      setFixedStartDate(() => {
        const newFixedStartDates: FixedStartDate = {};
        rangeSelectOptions.map(({ value, startDate }) => Object.assign(newFixedStartDates, { [value.key]: startDate() }));
        return newFixedStartDates;
      });
    }, Time.seconds(1));
    return () => clearInterval(updateTime);
  }, []);

  useEffect(() => {
    if (rangeSelection === RangeSelectValues.PICK_CUSTOM_RANGE.key) return;

    rangeSelectOptions.map((option: RangeSelectOption) => {
      if (option.value.key !== rangeSelection) return;
      onDateRangeChanged(option.startDate().toJSDate(), option.endDate().toJSDate());
      setSavedSelection(rangeSelection);
    });
  }, [rangeSelection]); // adding rangeSelectOptions would cause infinite re-render because startDate() and endDate() would return new time

  const captionStyles = useResponsiveText('0.75rem', 30, {
    minFontSize: '0.65rem',
    maxFontSize: '0.75rem',
    textLengthThreshold: 30,
  });

  return (
    <Box width={hour12 ? '330px' : '280px'}>
      <FormControl fullWidth size="small" ref={ref}>
        <InputLabel data-chromatic="ignore" id="datepicker-label">{textLabel}</InputLabel>
        <StyledRangeSelect
          label={textLabel}
          labelId="datepicker-label"
          onChange={(e) => setRangeSelection((e.target.value as RangeSelectValues['key']))}
          renderValue={(value) => renderTitle(value as RangeSelectValues['key'])}
          value={rangeSelection}
          data-testid={props['data-testid']}
          inputProps={{
            'data-testid': `${props['data-testid'] ?? 'date-time-range-select'}-input`,
          }}
        >
          {rangeSelectOptions.map((option: RangeSelectOption, index: number) => {
            return (
              <MenuItem key={index} value={option.value.key} data-testid={testid`item-${option.value.key}`}>
                <Stack>
                  <Typography>{option.label}</Typography>
                  <Typography
                    color="common.black"
                    variant="caption"
                    style={{
                      opacity: 0.54,
                      fontSize: captionStyles.fontSize,
                      lineHeight: captionStyles.lineHeight,
                      wordBreak: captionStyles.wordBreak,
                      whiteSpace: captionStyles.whiteSpace,
                      overflow: captionStyles.overflow,
                      textOverflow: captionStyles.textOverflow,
                    }}
                  >
                    {renderSubText(fixedStartDate?.[option.value.key] ?? option.startDate(), fixedEndDate)}
                  </Typography>
                </Stack>
              </MenuItem>
            );
          })}
          <Divider />
          <MenuItem
            key='item-custom'
            onClick={() => openDateTimePicker()}
            value="pick-custom-range"
            data-testid="item-custom"
          >
            <Stack direction="row" spacing={2} py={isCustomRangeSet ? 0 : 1}>
              <Icon icon="Calendar" style={{ alignSelf: 'center' }} />
              <Stack>
                <Typography>
                  {isCustomRangeSet ? textChangeRange : textPickRange}
                </Typography>
                {isCustomRangeSet &&
                  <Typography
                    color="common.black"
                    variant="caption"
                    style={{
                      opacity: 0.54,
                      fontSize: captionStyles.fontSize,
                      lineHeight: captionStyles.lineHeight,
                      wordBreak: captionStyles.wordBreak,
                      whiteSpace: captionStyles.whiteSpace,
                      overflow: captionStyles.overflow,
                      textOverflow: captionStyles.textOverflow,
                    }}
                  >
                    {renderSubText(savedStartDate, savedEndDate)}
                  </Typography>
                }
              </Stack>
            </Stack>
          </MenuItem>
        </StyledRangeSelect>
      </FormControl>
    </Box >
  );
});

RangeSelect.displayName = 'RangeSelect';
